import React, { useState } from 'react';
import styled from 'styled-components';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import session from '../../../App/session';
import { SAVES_TYPES } from 'right-revenue-microservice-sdk/lib/constants';
import injectSaga from '../../../../utils/redux/injectSaga';
import injectReducer from '../../../../utils/redux/injectReducer';

import { DOMAIN } from '../../constants';
import reducer from '../../reducer';
import saga from '../../saga';
import { v4 as uuidv4 } from 'uuid';

import { getCSVData, getTotalCpor } from './utils';
import * as actions from '../../actions';
import { Affix } from 'antd';

import RoomBreakdown from './RoomBreakdown';
import CostsList from './CostsList';
import FormActions from '../common/FormActions';
import getSymbolFromCurrency from 'currency-symbol-map';

import './occupiedRoom.style.css';
import 'react-toggle/style.css';

const Container = styled.div`
    /* Extra small devices (phones, 600px and down) */
    @media only screen and (max-width: 600px) {
        font-size: 45%;
    }

    /* Small devices (portrait tablets and large phones, 600px and up) */
    @media only screen and (min-width: 600px) {
        font-size: 50%;
    }

    /* Medium devices (landscape tablets, 768px and up) */
    @media only screen and (min-width: 768px) {
        font-size: 55%;
    }

    /* Large devices (laptops/desktops, 992px and up) */
    @media only screen and (min-width: 992px) {
        font-size: 62.5%;
    }

    /* Extra large devices (large laptops and desktops, 1200px and up) */
    @media only screen and (min-width: 1200px) {
        font-size: 62.5%;
    }
    padding: 2em;
`;

const HeadPanel = styled.div`
    left: 1rem;
    right: 1rem;
    padding: 1rem;
    background-color: white;
    border: 0.1em solid #eaeaea;
    box-shadow: 0 0.2em 0.4em 0 rgba(215, 215, 215, 0.5);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: auto;
    margin-bottom: 1em;
`;

const Panel = styled.div`
    padding: 1.5em;
    margin-bottom: 9em;
    background-color: white;
    border: 0.1em solid #eaeaea;
    box-shadow: 0 0.2em 0.4em 0 rgba(215, 215, 215, 0.5);
    overflow-y: hidden;
`;

const Title = styled.div`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.5625rem;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
`;
const OccupiedRoomForm = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const dispatch = useDispatch();

    const checkForErrors = (name, value, formSection) => {
        if (formSection === 'roomBreakdown' && name === 'daysOpenPerPeriod') {
            if (value > 366) {
                setErrorMessage('Days open per period cannot be greater than 366');
                return true;
            }
            setErrorMessage('');
            return false;
        }
        if (formSection === 'roomBreakdown' && name === 'averageOccupancy') {
            if (value > 100) {
                setErrorMessage('Average Occupancy cannot be greater than 100%');
                return true;
            }
            setErrorMessage('');
            return false;
        }
        return false;
    };

    const onToggleSection = (sectionName, value) => {
        dispatch(actions.updateSection(sectionName, value));
    };

    const onChangeRoomBreakdown = (fieldName, value) => {
        const hasError = checkForErrors(fieldName, value, 'roomBreakdown');
        if (!hasError) {
            dispatch(actions.updateRoomBreakdownValue(fieldName, value));
        }
    };

    const onToggleSectionItem = (sectionName, fieldId, value) => {
        dispatch(actions.updateFormValue(sectionName, fieldId, 'TOGGLE', value));
    };

    const onAddSectionItem = (sectionName) => {
        dispatch(actions.updateFormValue(sectionName, uuidv4(), 'CREATE'));
    };

    const onRemoveSectionItem = (sectionName, fieldId) => {
        dispatch(actions.updateFormValue(sectionName, fieldId, 'DELETE'));
    };

    const onChangeSectionItemValue = (sectionName, fieldId, value) => {
        dispatch(actions.updateFormValue(sectionName, fieldId, 'VALUE', value));
    };

    const onChangeSectionItemName = (sectionName, fieldId, value) => {
        dispatch(actions.updateFormValue(sectionName, fieldId, 'NAME', value));
    };

    const onChangeSectionItemPercentage = (sectionName, fieldId, value) => {
        dispatch(actions.updateFormValue(sectionName, fieldId, 'PERCENTAGE', value));
    };

    const currency = useSelector(session.selectors.selectCurrency);

    const formData = useSelector((state) => state.formData.occupiedCostCalculator);

    const totalCpor = getTotalCpor(formData);
    const csvData = getCSVData(formData);
    const currencyIcon = getSymbolFromCurrency(currency);
    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? 'white' : 'black',
            backgroundColor: state.isSelected ? '#125651' : 'white',
        }),
        control: (provided, state) => ({
            ...provided,
            border: state.isFocused ? '0.1em solid #30b79c' : '0.1em solid #D3D3D3',
            boxShadow: state.isFocused ? '0 0 0.5em rgba(48, 181, 155, 0.6)' : 'none',
            '&:hover': {
                border: '0.1em solid #30B79C',
            },
        }),
    };

    const sections = [
        {
            name: 'roomConsumables',
            texts: {
                title: 'Room Consumables',
                addBtn: 'Add',
            },
            allowTogglingItems: false,
            hidePercentage: true,
            allowDeletingPresets: false,
        },
        {
            name: 'fixedCosts',
            texts: {
                title: 'Fixed Costs',
                addBtn: 'Add',
            },
            allowTogglingItems: true,
            hidePercentage: false,
            allowDeletingPresets: true,
        },
        {
            name: 'salaryCosts',
            texts: {
                title: 'Salary Costs',
                addBtn: 'Add',
            },
            allowTogglingItems: true,
            hidePercentage: false,
            allowDeletingPresets: true,
        },
        {
            name: 'commission',
            texts: {
                title: 'Commission Paid',
                addBtn: 'Add',
            },
            allowTogglingItems: true,
            hidePercentage: true,
            allowDeletingPresets: true,
        },
        {
            name: 'variables',
            texts: {
                title: 'Variables',
                addBtn: 'Add',
            },
            allowTogglingItems: true,
            hidePercentage: false,
            allowDeletingPresets: true,
        },
    ];

    const corLabel = `${currencyIcon}${totalCpor === 'Infinity' ? 'Please complete room breakdown' : totalCpor}`;
    return (
        <Container>
            <Affix offsetTop={80}>
                <HeadPanel>
                    <Title>Cost Calculator</Title>
                    <Title>{`Total Cost of Occupied Room: ${corLabel}`}</Title>
                </HeadPanel>
            </Affix>

            <Panel>
                <RoomBreakdown formData={formData} onInputChange={onChangeRoomBreakdown} />
                {errorMessage && <p style={{ color: '#d32f2f' }}>{errorMessage}</p>}
                {sections.map((section) => (
                    <CostsList
                        key={section.name}
                        formData={formData}
                        currency={currencyIcon}
                        sectionName={section.name}
                        texts={section.texts}
                        onToggleSection={onToggleSection}
                        onToggleField={onToggleSectionItem}
                        onChangeFieldValue={onChangeSectionItemValue}
                        onChangeFieldName={onChangeSectionItemName}
                        onChangeFieldPercentage={onChangeSectionItemPercentage}
                        onAddSectionItem={onAddSectionItem}
                        onRemoveSectionItem={onRemoveSectionItem}
                        hidePercentage={section.hidePercentage}
                        allowDeletingPresets={section.allowDeletingPresets}
                        allowTogglingItems={section.allowTogglingItems}
                        selectStyles={selectStyles}
                    />
                ))}
            </Panel>
            <FormActions
                formType={SAVES_TYPES.COST_CALCULATOR}
                extractStateToSave={(state) => ({
                    occupiedCostCalculator: state.occupiedCostCalculator,
                })}
                exportedCSVData={csvData}
                exportedFileName="right-revenue-cost-calculator.csv"
            />
        </Container>
    );
};

const withReducer = injectReducer({ key: DOMAIN, reducer });
const withSaga = injectSaga({ key: DOMAIN, saga });

export default compose(withReducer, withSaga)(OccupiedRoomForm);
